import { parseContractComplianceError, RuleExceptionRequests } from 'api/requests/contract-compliance-requests';
import { ScopeLevel } from 'constants/ScopeLevel';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__CONCURRENT_CHANGE,
  CONTRACT_COMPLIANCE__DUPLICATE_EXCEPTION,
  CONTRACT_COMPLIANCE__EXCEPTION_REMOVED,
  CONTRACT_COMPLIANCE__EXCEPTION_SAVED,
  CONTRACT_COMPLIANCE__NOT_FOUND,
  CONTRACT_COMPLIANCE__PANEL_LOAD_ERROR,
  CONTRACT_COMPLIANCE__SCOPE_ENTITY_NOT_FOUND,
  CONTRACT_COMPLIANCE__TOO_MANY_SCOPE_ENTITIES,
  GENERAL__ERROR_MESSAGE,
} from 'translations/constants';
import {
  ContractComplianceRule,
  IRemoveRuleException,
  IRuleException,
  ParsedContractComplianceError,
} from 'types/contract-compliance/IContractCompliance';
import { MAX_SCOPE_ENTITIES_PER_ADD } from '../constants';
import { EMPTY_INTERVAL, IntervalDraft, toIntervalDraft, toIntervalPayload, validateInterval } from '../interval';
import { getContractComplianceErrorMessage, getOffendingEntityIds } from '../utils';
import { ExceptionPanelCallbacks, PanelStatus } from './types';
import { useJustification } from './useJustification';
import { useScopeEntities } from './useScopeEntities';

export type ExceptionPanelMode = 'add' | 'edit';

/** Scope level preselected when adding, as in the mockup. */
const DEFAULT_SCOPE_LEVEL = ScopeLevel.JOINT_COMMISSION;

/**
 * Side panel for adding exceptions (one or more entities in one all-or-nothing call) or editing one exception's
 * scope, plus removing it behind the static disclaimer.
 */
export const useRuleExceptionPanel = (
  requests: RuleExceptionRequests,
  withInterval: boolean,
  callbacks: ExceptionPanelCallbacks,
) => {
  const [t] = useTranslation();
  const justification = useJustification();
  const scopeEntities = useScopeEntities();
  const [mode, setMode] = useState<ExceptionPanelMode>('add');
  const [rule, setRule] = useState<ContractComplianceRule | null>(null);
  const [status, setStatus] = useState<PanelStatus>('closed');
  const [exception, setException] = useState<IRuleException | null>(null);
  const [scopeLevel, setScopeLevelState] = useState<ScopeLevel>(DEFAULT_SCOPE_LEVEL);
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [interval, setIntervalDraft] = useState<IntervalDraft>(EMPTY_INTERVAL);
  const [showErrors, setShowErrors] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const savingRef = useRef(false);
  // Bumped on every open and close, so answers for a panel the user has left are dropped.
  const sessionRef = useRef(0);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const { reset: resetJustification, toPayload } = justification;

  // A save error stays until the user's first edit after it (reloads after a conflict do not count).
  const { text: justificationText, document: justificationDocument } = justification;
  useEffect(() => {
    setSaveErrorMessage(null);
  }, [justificationText, justificationDocument]);
  const { load: loadEntities, getEntityNames } = scopeEntities;

  const reset = useCallback(() => {
    sessionRef.current += 1;
    setRule(null);
    setStatus('closed');
    setException(null);
    setScopeLevelState(DEFAULT_SCOPE_LEVEL);
    setSelectedEntityIds([]);
    setIntervalDraft(EMPTY_INTERVAL);
    setShowErrors(false);
    setIsConfirmOpen(false);
    setIsRemoveOpen(false);
    setLoadErrorMessage(null);
    setSaveErrorMessage(null);
    resetJustification();
  }, [resetJustification]);

  const loadException = useCallback(
    async (id: number, session: number) => {
      setStatus('loading');
      setLoadErrorMessage(null);
      try {
        const value = await requests.get(id);
        if (session !== sessionRef.current) return;
        setException(value);
        setScopeLevelState(value.scopeLevel);
        setSelectedEntityIds(value.scopeEntityId !== null ? [String(value.scopeEntityId)] : []);
        setIntervalDraft(toIntervalDraft(value.startTime, value.endTime));
        loadEntities(value.scopeLevel);
        setStatus('ready');
      } catch (error) {
        if (session !== sessionRef.current) return;
        const parsed = parseContractComplianceError(error);
        const message = getContractComplianceErrorMessage(t, parsed, CONTRACT_COMPLIANCE__PANEL_LOAD_ERROR);
        if (parsed.key === CONTRACT_COMPLIANCE__NOT_FOUND) {
          reset();
          callbacksRef.current.onGone(message);
          return;
        }
        setLoadErrorMessage(message);
        setStatus('loadError');
      }
    },
    [loadEntities, requests, reset, t],
  );

  const openAdd = useCallback(
    (target: ContractComplianceRule) => {
      reset();
      setMode('add');
      setRule(target);
      setStatus('ready');
      loadEntities(DEFAULT_SCOPE_LEVEL);
    },
    [loadEntities, reset],
  );

  const openEdit = useCallback(
    (row: IRuleException) => {
      reset();
      setMode('edit');
      setRule(row.rule);
      setException(row);
      loadException(row.id, sessionRef.current);
    },
    [loadException, reset],
  );

  const retryLoad = useCallback(() => {
    if (exception) loadException(exception.id, sessionRef.current);
  }, [exception, loadException]);

  const setScopeLevel = useCallback(
    (level: ScopeLevel) => {
      setScopeLevelState(level);
      setSelectedEntityIds([]);
      setSaveErrorMessage(null);
      loadEntities(level);
    },
    [loadEntities],
  );

  const selectEntities = useCallback((ids: string[]) => {
    setSelectedEntityIds(ids);
    setSaveErrorMessage(null);
  }, []);

  const setIntervalField = useCallback((field: keyof IntervalDraft, value: string) => {
    setIntervalDraft(previous => ({ ...previous, [field]: value }));
    setSaveErrorMessage(null);
  }, []);

  const retryEntities = useCallback(() => loadEntities(scopeLevel, true), [loadEntities, scopeLevel]);

  const isComplete = scopeLevel === ScopeLevel.AGENCY || selectedEntityIds.length > 0;

  const getFieldError = (): string | null => {
    if (scopeLevel === ScopeLevel.AGENCY) return null;
    if (selectedEntityIds.length > MAX_SCOPE_ENTITIES_PER_ADD) {
      return t(CONTRACT_COMPLIANCE__TOO_MANY_SCOPE_ENTITIES, { max: MAX_SCOPE_ENTITIES_PER_ADD });
    }
    return null;
  };
  const validationError = getFieldError();
  const fieldError = showErrors ? validationError : null;
  const intervalValidationErrors = withInterval ? validateInterval(interval, false) : {};
  const hasIntervalErrors = Object.keys(intervalValidationErrors).length > 0;
  const intervalErrors = showErrors ? intervalValidationErrors : {};

  const requestSave = useCallback(() => {
    if (!isComplete) return;
    setShowErrors(true);
    if (status !== 'ready' || savingRef.current || justification.isUploading || validationError) return;
    if (hasIntervalErrors) return;
    setIsConfirmOpen(true);
  }, [isComplete, status, justification.isUploading, validationError, hasIntervalErrors]);

  const cancelConfirm = useCallback(() => setIsConfirmOpen(false), []);

  /** The API message for these is generic, so the offending entities are named from the dropdown data. */
  const describeEntityError = useCallback(
    (parsed: ParsedContractComplianceError, message: string): string => {
      const ids = getOffendingEntityIds(parsed);
      return ids.length > 0 ? `${message}: ${getEntityNames(scopeLevel, ids).join(', ')}` : message;
    },
    [getEntityNames, scopeLevel],
  );

  /** Shared failure handling for save and remove. Returns false when the error is left to the caller. */
  const handleWriteError = useCallback(
    (parsed: ParsedContractComplianceError, message: string, session: number, id: number | null): boolean => {
      const stillShowing = session === sessionRef.current;
      if (parsed.key === CONTRACT_COMPLIANCE__NOT_FOUND) {
        if (stillShowing) reset();
        callbacksRef.current.onGone(message);
        return true;
      }
      if (!stillShowing) {
        if (parsed.key === CONTRACT_COMPLIANCE__CONCURRENT_CHANGE) callbacksRef.current.onStale();
        callbacksRef.current.onError(message);
        return true;
      }
      if (parsed.key === CONTRACT_COMPLIANCE__CONCURRENT_CHANGE && id !== null) {
        // Reload so the user sees the other change and the next save carries the fresh rowVersion.
        setSaveErrorMessage(message);
        callbacksRef.current.onStale();
        loadException(id, session);
        return true;
      }
      return false;
    },
    [loadException, reset],
  );

  const confirmSave = useCallback(async () => {
    if (savingRef.current || rule === null) return;
    const session = sessionRef.current;
    savingRef.current = true;
    setIsSaving(true);
    setIsConfirmOpen(false);
    setSaveErrorMessage(null);
    const isAgency = scopeLevel === ScopeLevel.AGENCY;
    const intervalPayload = withInterval ? toIntervalPayload(interval) : {};
    try {
      if (mode === 'add') {
        await requests.add({
          rule,
          scopeLevel,
          scopeEntityIds: isAgency ? null : selectedEntityIds.map(Number),
          ...intervalPayload,
          ...toPayload(),
        });
      } else if (exception) {
        await requests.update(exception.id, {
          scopeLevel,
          scopeEntityId: isAgency ? null : Number(selectedEntityIds[0]),
          rowVersion: exception.rowVersion,
          ...intervalPayload,
          ...toPayload(),
        });
      }
      if (session === sessionRef.current) reset();
      callbacksRef.current.onSaved(CONTRACT_COMPLIANCE__EXCEPTION_SAVED);
    } catch (error) {
      const parsed = parseContractComplianceError(error);
      const message = getContractComplianceErrorMessage(t, parsed, GENERAL__ERROR_MESSAGE);
      if (!handleWriteError(parsed, message, session, exception?.id ?? null)) {
        // Nothing was saved (all or nothing): the panel keeps the whole selection and shows the error, naming what
        // blocked it, at its top. Not the bottom-right banner: in a side panel it covers the Save button.
        const isEntityError =
          parsed.key === CONTRACT_COMPLIANCE__DUPLICATE_EXCEPTION ||
          parsed.key === CONTRACT_COMPLIANCE__SCOPE_ENTITY_NOT_FOUND;
        setSaveErrorMessage(isEntityError ? describeEntityError(parsed, message) : message);
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [
    rule,
    scopeLevel,
    mode,
    selectedEntityIds,
    interval,
    withInterval,
    requests,
    exception,
    toPayload,
    reset,
    handleWriteError,
    describeEntityError,
    t,
  ]);

  const requestRemove = useCallback(() => {
    if (mode === 'edit' && status === 'ready' && !savingRef.current) setIsRemoveOpen(true);
  }, [mode, status]);

  const cancelRemove = useCallback(() => setIsRemoveOpen(false), []);

  // The retry closure must call the latest runRemove, not the one captured when the banner was shown.
  const runRemoveRef = useRef<
    (target: IRuleException, payload: IRemoveRuleException, session: number) => Promise<void>
  >(async () => undefined);

  /**
   * Shared by the disclaimer's Confirm and the Retry in the failure banner. The retry can run after the panel closed,
   * so the target and payload are passed in, and the same double-submit guard applies.
   */
  const runRemove = useCallback(
    async (target: IRuleException, payload: IRemoveRuleException, session: number) => {
      if (savingRef.current) return;
      savingRef.current = true;
      setIsSaving(true);
      setIsRemoveOpen(false);
      setSaveErrorMessage(null);
      try {
        await requests.remove(target.id, payload);
        if (session === sessionRef.current) reset();
        callbacksRef.current.onSaved(CONTRACT_COMPLIANCE__EXCEPTION_REMOVED);
      } catch (error) {
        const parsed = parseContractComplianceError(error);
        const message = getContractComplianceErrorMessage(t, parsed, GENERAL__ERROR_MESSAGE);
        if (!handleWriteError(parsed, message, session, target.id)) {
          // The exception stays in place; the bottom-right banner offers a retry.
          callbacksRef.current.onRemoveFailed(message, () => {
            runRemoveRef.current(target, payload, session);
          });
        }
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [requests, reset, handleWriteError, t],
  );
  runRemoveRef.current = runRemove;

  const confirmRemove = useCallback(() => {
    if (!exception) return;
    // Captured now: the retry from the banner may run after the panel has closed.
    runRemove(exception, { rowVersion: exception.rowVersion, ...toPayload() }, sessionRef.current);
  }, [exception, toPayload, runRemove]);

  const close = useCallback(() => {
    reset();
    callbacksRef.current.onClosed();
  }, [reset]);

  return {
    mode,
    rule,
    status,
    exception,
    scopeLevel,
    setScopeLevel,
    selectedEntityIds,
    selectEntities,
    scopeEntities,
    retryEntities,
    fieldError,
    isComplete,
    isSaving,
    isConfirmOpen,
    isRemoveOpen,
    loadErrorMessage,
    saveErrorMessage,
    interval,
    setIntervalField,
    intervalErrors,
    justification,
    openAdd,
    openEdit,
    retryLoad,
    requestSave,
    cancelConfirm,
    confirmSave,
    requestRemove,
    cancelRemove,
    confirmRemove,
    close,
  };
};

export type UseRuleExceptionPanelResult = ReturnType<typeof useRuleExceptionPanel>;
